import type React from "react";
import { Controller, useFormContext } from "react-hook-form";
import type { OnboardingFormData } from "../../../types/onboarding";
import { ASSETS, CHAINS, CUSTODIAL_TYPES } from "../../../types/onboarding";
import FieldError from "../../FieldError";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

const selectBase: React.CSSProperties = {
	width: "100%",
	padding: "0.5rem 0.75rem",
	borderRadius: "var(--border-radius)",
	border: "1px solid var(--color-border)",
	background: "var(--color-white)",
	color: "var(--color-text-primary)",
	fontSize: "0.875rem",
};

const errBorder: React.CSSProperties = {
	borderColor: "var(--color-error-500)",
};

/**
 * Step 1: Company & Wallet Info
 *
 * Mirrors the Add Wallet form fields — company basics + wallet config.
 * No KYB-level compliance or document uploads at this stage.
 */
const CompanyWalletStep: React.FC = () => {
	const {
		control,
		watch,
		formState: { errors },
	} = useFormContext<OnboardingFormData>();

	const products = watch("products") || [];

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
			<h2
				style={{
					fontSize: "1.25rem",
					fontWeight: 600,
					color: "var(--color-text-primary)",
					marginBottom: "0.25rem",
				}}
			>
				Company & Wallet Information
			</h2>
			<p
				style={{
					fontSize: "0.85rem",
					color: "var(--color-text-secondary)",
					marginTop: "-0.75rem",
				}}
			>
				Tell us about your company and the wallet you'd like to set up.
			</p>

			{/* Company Name */}
			<div>
				<Label htmlFor="company_name">Company Name *</Label>
				<Controller
					name="company_name"
					control={control}
					render={({ field }) => (
						<Input
							id="company_name"
							placeholder="Your company name"
							{...field}
							style={
								errors.company_name
									? { borderColor: "var(--color-error-500)" }
									: undefined
							}
						/>
					)}
				/>
				<FieldError error={errors.company_name} />
			</div>

			{/* Wallet Name */}
			<div>
				<Label htmlFor="wallet_name">Wallet Name *</Label>
				<Controller
					name="wallet_name"
					control={control}
					render={({ field }) => (
						<Input
							id="wallet_name"
							placeholder="A name for your wallet"
							{...field}
							style={
								errors.wallet_name
									? { borderColor: "var(--color-error-500)" }
									: undefined
							}
						/>
					)}
				/>
				<FieldError error={errors.wallet_name} />
			</div>

			{/* Custodial / Chain / Asset row */}
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr 1fr",
					gap: "0.75rem",
				}}
			>
				<div>
					<Label htmlFor="custodial_type">Custodial Type *</Label>
					<Controller
						name="custodial_type"
						control={control}
						render={({ field }) => (
							<select
								id="custodial_type"
								{...field}
								style={{
									...selectBase,
									...(errors.custodial_type ? errBorder : {}),
								}}
							>
								{CUSTODIAL_TYPES.map((t) => (
									<option key={t} value={t}>
										{t}
									</option>
								))}
							</select>
						)}
					/>
					<FieldError error={errors.custodial_type} />
				</div>
				<div>
					<Label htmlFor="chain">Chain *</Label>
					<Controller
						name="chain"
						control={control}
						render={({ field }) => (
							<select
								id="chain"
								{...field}
								style={{ ...selectBase, ...(errors.chain ? errBorder : {}) }}
							>
								<option value="">Select chain</option>
								{CHAINS.map((c) => (
									<option key={c} value={c}>
										{c}
									</option>
								))}
							</select>
						)}
					/>
					<FieldError error={errors.chain} />
				</div>
				<div>
					<Label htmlFor="asset">Asset *</Label>
					<Controller
						name="asset"
						control={control}
						render={({ field }) => (
							<select
								id="asset"
								{...field}
								style={{ ...selectBase, ...(errors.asset ? errBorder : {}) }}
							>
								<option value="">Select asset</option>
								{ASSETS.map((a) => (
									<option key={a} value={a}>
										{a}
									</option>
								))}
							</select>
						)}
					/>
					<FieldError error={errors.asset} />
				</div>
			</div>

			{/* Products */}
			<div>
				<Label>Products *</Label>
				<div style={{ display: "flex", gap: "1.5rem", marginTop: "0.25rem" }}>
					{(["Deposit", "Withdraw"] as const).map((product) => (
						<Controller
							key={product}
							name="products"
							control={control}
							render={({ field }) => (
								<label
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.4rem",
										fontSize: "0.875rem",
										color: "var(--color-text-primary)",
										cursor: "pointer",
									}}
								>
									<input
										type="checkbox"
										checked={products.includes(product)}
										onChange={(e) => {
											const current = field.value || [];
											field.onChange(
												e.target.checked
													? [...current, product]
													: current.filter((p: string) => p !== product),
											);
										}}
										style={{
											accentColor: "var(--color-primary)",
											width: "1rem",
											height: "1rem",
										}}
									/>
									{product}
								</label>
							)}
						/>
					))}
				</div>
				<FieldError error={errors.products} />
			</div>

			{/* Country */}
			<div>
				<Label htmlFor="country_of_incorporation">
					Country of Incorporation *
				</Label>
				<Controller
					name="country_of_incorporation"
					control={control}
					render={({ field }) => (
						<Input
							id="country_of_incorporation"
							placeholder="e.g. United States"
							{...field}
							style={errors.country_of_incorporation ? errBorder : undefined}
						/>
					)}
				/>
				<FieldError error={errors.country_of_incorporation} />
			</div>

			{/* Website */}
			<div>
				<Label htmlFor="website">Website</Label>
				<Controller
					name="website"
					control={control}
					render={({ field }) => (
						<Input
							id="website"
							type="url"
							placeholder="https://example.com"
							{...field}
						/>
					)}
				/>
				<FieldError error={errors.website} />
			</div>

			{/* Business Description */}
			<div>
				<Label htmlFor="business_description">Business Description *</Label>
				<Controller
					name="business_description"
					control={control}
					render={({ field }) => (
						<textarea
							id="business_description"
							rows={3}
							placeholder="Briefly describe your business and how you plan to use MoneyGram services..."
							{...field}
							style={{
								...selectBase,
								resize: "vertical" as const,
								fontFamily: "inherit",
								...(errors.business_description ? errBorder : {}),
							}}
						/>
					)}
				/>
				<FieldError error={errors.business_description} />
			</div>
		</div>
	);
};

export default CompanyWalletStep;
